import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { LoadingButton } from '@/components/ui/loading-button';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { useFeedback } from '@/contexts/FeedbackContext';
import { Task } from '@/types/taskTypes';
import { UploadedImage } from '@/utils/imageUpload';
import { z } from 'zod';
import { useFormDraft } from '@/hooks/useFormDraft';
import { smartToast } from '@/utils/smartToast';

const feedbackSchema = z.object({
  description: z.string().trim().min(1, 'Feedback message is required').max(1000, 'Message must be less than 1000 characters'),
  priority: z.enum(['low', 'medium', 'high'])
});

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit?: (feedback: {
    description: string;
    priority: Task['priority'];
    userId?: string;
    timestamp: string;
    images?: UploadedImage[];
  }) => Promise<void>;
}

const FeedbackModalValidated: React.FC<FeedbackModalProps> = ({ isOpen, onClose, onSubmit }) => {
  const { submitFeedback } = useFeedback();
  const { values, setValues, clearDraft } = useFormDraft('feedback-form', {
    description: '',
    priority: 'low' as Task['priority']
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [charCount, setCharCount] = useState(0);

  const validateField = (field: keyof typeof values, value: any) => {
    try {
      // Validate just this field
      if (field === 'description') {
        feedbackSchema.shape.description.parse(value);
      } else if (field === 'priority') {
        feedbackSchema.shape.priority.parse(value);
      }
      
      setErrors(prev => ({ ...prev, [field]: '' }));
    } catch (error) {
      if (error instanceof z.ZodError) {
        setErrors(prev => ({ ...prev, [field]: error.errors[0].message }));
      }
    }
  };

  const handleDescriptionChange = (value: string) => {
    setValues({ ...values, description: value });
    setCharCount(value.length);
    validateField('description', value);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      feedbackSchema.parse(values);

      setIsSubmitting(true);
      
      const feedbackData = {
        description: values.description.trim(),
        priority: values.priority,
        timestamp: new Date().toISOString()
      };

      if (onSubmit) {
        await onSubmit(feedbackData);
      } else {
        await submitFeedback(feedbackData);
      }

      // Reset form
      setValues({ description: '', priority: 'low' });
      setCharCount(0);
      clearDraft();
      onClose();
    } catch (error) {
      if (error instanceof z.ZodError) {
        const fieldErrors: Record<string, string> = {};
        error.errors.forEach(err => {
          if (err.path[0]) {
            fieldErrors[err.path[0] as string] = err.message;
          }
        });
        setErrors(fieldErrors);
      } else {
        console.error('Error submitting feedback:', error);
        smartToast.error(error, { type: 'save', resource: 'feedback' });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (values.description.trim()) {
      // Draft is auto-saved by useFormDraft
      smartToast.info('Draft saved', 'Your feedback was saved. Come back anytime to finish.');
    }
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="w-[calc(100%-2rem)] sm:max-w-md max-h-[90vh] overflow-y-auto overscroll-contain">
        <DialogHeader>
          <DialogTitle>Send Feedback</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <div className="flex justify-between items-center mb-1.5">
              <Label htmlFor="feedback">Your feedback</Label>
              <span className="text-xs text-muted-foreground">
                {charCount}/1000
              </span>
            </div>
            <Textarea
              id="feedback"
              value={values.description}
              onChange={(e) => handleDescriptionChange(e.target.value)}
              onBlur={(e) => validateField('description', e.target.value)}
              placeholder="Tell us what you think or report an issue..."
              className={`min-h-[100px] ${errors.description ? 'border-destructive' : ''}`}
              required
              maxLength={1000}
            />
            {errors.description && (
              <p className="text-xs text-destructive mt-1">{errors.description}</p>
            )}
          </div>

          <div>
            <Label htmlFor="priority">Priority</Label>
            <Select 
              value={values.priority} 
              onValueChange={(value: Task['priority']) => setValues({ ...values, priority: value })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="low">Low</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="high">High</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex justify-end gap-2">
            <Button 
              type="button" 
              variant="outline" 
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <LoadingButton 
              type="submit" 
              disabled={!values.description.trim()}
              loading={isSubmitting}
              loadingText="Submitting..."
            >
              Submit Feedback
            </LoadingButton>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default FeedbackModalValidated;
